import { useQuery } from "@tanstack/react-query";
import { Navigate } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { OpaqueIdSchema, type MeResponse, type OrganizationSummary } from "../../shared/schemas";
import { ApiClientError, navigateToWorkspace } from "../api/client";
import { meQuery, useLogoutMutation } from "../api/queries";
import { cn } from "../lib/utils";
import { workspaceAccentStyle } from "../lib/workspace-accent";
import { resolveShellViewMode } from "../lib/organization-context";
import { DocumentMetadata } from "./DocumentMetadata";
import { DeploymentUpdateNotice } from "./DeploymentUpdates";
import { useDeploymentUpdates } from "./deployment-update-context";
import { useOnlineStatus } from "./OfflineState";
import type { ShellNavigationItem } from "./PrimaryNavigation";
import { ShellHeader } from "./ShellHeader";
import { ErrorShell, LoadingShell, OfflineShell } from "./ShellQueryState";
import { useShellViewMode } from "./ShellViewModeProvider";

function WorkspaceTabMetadata({
  workspace,
}: {
  workspace: { id: string; name: string; faviconVersion: number | null } | null | undefined;
}) {
  const faviconHref =
    workspace?.faviconVersion && workspace.id
      ? `/api/workspaces/${encodeURIComponent(workspace.id)}/favicon?v=${workspace.faviconVersion}`
      : undefined;
  return (
    <DocumentMetadata
      faviconHref={faviconHref}
      title={workspace?.name ? `${workspace.name} Tools` : "CCBox"}
    />
  );
}

interface AuthenticatedShellProps {
  children: ReactNode;
  activeOrganizationId?: string | undefined;
  onOrganizationChange?: ((organizationId: string) => void) | undefined;
  backToToolsOrganizationId?: string | undefined;
  activeNavigationItem?: ShellNavigationItem | null | undefined;
  fillViewport?: boolean | undefined;
  requiresAdminView?: boolean | undefined;
}

interface AuthenticatedShellLayoutProps extends AuthenticatedShellProps {
  activeOrganization: OrganizationSummary | undefined;
  isOnline: boolean;
  isSigningOut: boolean;
  me: MeResponse;
  onSignOut: () => void;
  platformUpdateAvailable: boolean;
  refreshPlatform: () => void;
  setViewMode: ReturnType<typeof useShellViewMode>["setConsultantViewMode"];
  viewMode: ReturnType<typeof resolveShellViewMode>;
}

function AuthenticatedShellLayout(props: AuthenticatedShellLayoutProps) {
  const handleOrganizationChange =
    props.onOrganizationChange ??
    ((organizationId: string) => {
      navigateToWorkspace(organizationId, `/?org=${encodeURIComponent(organizationId)}`);
    });
  return (
    <div
      className={cn(
        "shell-root flex min-h-dvh flex-col bg-background",
        props.activeOrganization?.primaryColor && "workspace-accent",
        props.fillViewport && "h-dvh overflow-hidden",
      )}
      style={workspaceAccentStyle(props.activeOrganization?.primaryColor)}
    >
      <WorkspaceTabMetadata workspace={props.activeOrganization} />
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <ShellHeader
        activeNavigationItem={props.activeNavigationItem ?? "tools"}
        activeOrganization={props.activeOrganization}
        activeOrganizationId={props.activeOrganizationId}
        backToToolsOrganizationId={props.backToToolsOrganizationId}
        isOnline={props.isOnline}
        isSigningOut={props.isSigningOut}
        organizations={props.me.organizations}
        user={props.me.user}
        viewMode={props.viewMode}
        onOrganizationChange={handleOrganizationChange}
        onSignOut={props.onSignOut}
        onViewModeChange={props.setViewMode}
      />
      {props.platformUpdateAvailable ? (
        <DeploymentUpdateNotice kind="platform" onRefresh={props.refreshPlatform} />
      ) : null}
      {props.children}
    </div>
  );
}

interface AuthenticatedContentProps extends AuthenticatedShellProps {
  isOnline: boolean;
  isSigningOut: boolean;
  me: MeResponse;
  onSignOut: () => void;
  platformUpdateAvailable: boolean;
  refreshPlatform: () => void;
  setViewMode: ReturnType<typeof useShellViewMode>["setConsultantViewMode"];
  viewModePreference: ReturnType<typeof useShellViewMode>["consultantViewMode"];
}

function AuthenticatedContent(props: AuthenticatedContentProps) {
  const activeOrganization = props.me.organizations.find(
    (organization) => organization.id === props.activeOrganizationId,
  );
  const viewMode = resolveShellViewMode(
    props.me.user,
    activeOrganization,
    props.viewModePreference,
  );
  if (props.requiresAdminView && viewMode === "user") {
    return <Navigate replace search={{ org: props.activeOrganizationId }} to="/" />;
  }
  return (
    <AuthenticatedShellLayout
      {...props}
      activeNavigationItem={props.activeNavigationItem ?? "tools"}
      activeOrganization={activeOrganization}
      fillViewport={props.fillViewport ?? false}
      setViewMode={props.setViewMode}
      viewMode={viewMode}
    />
  );
}

interface ShellQueryBoundaryProps extends AuthenticatedShellProps {
  error: Error | null;
  isError: boolean;
  isOnline: boolean;
  isPending: boolean;
  me: MeResponse | undefined;
  renderContent: (me: MeResponse) => ReactNode;
}

function LoginRedirect() {
  const requestedOrganization = OpaqueIdSchema.safeParse(
    new URLSearchParams(window.location.search).get("org"),
  );
  return (
    <Navigate
      replace
      search={requestedOrganization.success ? { org: requestedOrganization.data } : {}}
      to="/login"
    />
  );
}

function ShellQueryBoundary(props: ShellQueryBoundaryProps) {
  if (props.error instanceof ApiClientError && props.error.status === 401) {
    return <LoginRedirect />;
  }
  if (!props.isOnline && !props.me) return <OfflineShell />;
  if (props.isPending) {
    const customHostname =
      window.location.hostname !== "app.ccbox.ai" && window.location.hostname.endsWith(".ccbox.ai");
    return <LoadingShell suppressBrand={customHostname} />;
  }
  if (props.isError || !props.me) return <ErrorShell />;
  return props.renderContent(props.me);
}

export function AuthenticatedShell(props: AuthenticatedShellProps) {
  const isOnline = useOnlineStatus();
  const me = useQuery({ ...meQuery, enabled: isOnline });
  const logout = useLogoutMutation();
  const { consultantViewMode, setConsultantViewMode } = useShellViewMode();
  const { platformUpdateAvailable, refreshPlatform } = useDeploymentUpdates();
  return (
    <ShellQueryBoundary
      {...props}
      error={me.error}
      isError={me.isError}
      isOnline={isOnline}
      isPending={me.isPending}
      me={me.data}
      renderContent={(meData) => {
        const signOut = () => {
          logout.mutate(undefined, {
            onSuccess: () => {
              window.location.assign("https://app.ccbox.ai/login");
            },
          });
        };
        return (
          <AuthenticatedContent
            {...props}
            isOnline={isOnline}
            isSigningOut={logout.isPending}
            me={meData}
            platformUpdateAvailable={platformUpdateAvailable}
            refreshPlatform={refreshPlatform}
            setViewMode={setConsultantViewMode}
            viewModePreference={consultantViewMode}
            onSignOut={signOut}
          />
        );
      }}
    />
  );
}
